import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Star, MapPin, Globe, Clock, Languages, Shield, CheckCircle,
  MessageSquare, Mail, User, Briefcase, Building, Phone, Mail as MailIcon,
  Home, Users, FileText, MessageCircle, User2, Settings2, ChartAreaIcon,
  FileCheck, Bell, Shield as ShieldIcon, Wallet, Lock, AlertCircle, LogOut,
  Bookmark, UserSearchIcon, Menu
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/Button";
import FavoriteButton from "../components/ui/FavoriteButton";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useSelector } from "react-redux";
import { useAppDispatch, getFavoriteByUserId, getUserById } from "../redux";
import { MdReviews } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";

const ClientFavorites = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [activeTab, setActiveTab] = useState("professionals");
  
  const { user, isAuthenticated } = useSelector((state: any) => state.auth);
  const { currentUserFavorite, loading } = useSelector((state: any) => state.favorites);
  const dispatch = useAppDispatch();
  const [favoriteProfessionalsData, setFavoriteProfessionalsData] = useState([]);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);

  // Load favorites when component mounts
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      dispatch(getFavoriteByUserId(user.id));
    }
  }, [isAuthenticated, user?.id, dispatch]);

  // Fetch user data when favorites are loaded
  useEffect(() => {
    const loadUserData = async () => {
      if (isAuthenticated && currentUserFavorite?.userIds) {
        const userIds = currentUserFavorite.userIds.map(user => user.id);
        const userData = await fetchUserDataByIds(userIds);
        setFavoriteProfessionalsData(userData);
      } else if (!isAuthenticated) {
        // For unauthenticated users, get from localStorage
        try {
          const localFavorites = localStorage.getItem("favoriteUserIds");
          const localUserIds = localFavorites ? JSON.parse(localFavorites) : [];
          const userData = await fetchUserDataByIds(localUserIds);
          setFavoriteProfessionalsData(userData);
        } catch (error) {
          setFavoriteProfessionalsData([]);
        }
      } else {
        setFavoriteProfessionalsData([]);
      }
    };

    loadUserData();
  }, [currentUserFavorite, isAuthenticated]);

  // Fetch user data for favorite professional IDs
  const fetchUserDataByIds = async (userIds: string[]) => {
    if (userIds.length === 0) return [];
    
    setProfessionalsLoading(true);
    try {
      const promises = userIds.map(async (userId) => {
        try {
          const result = await dispatch(getUserById({ id: userId, isShowLoader: false })).unwrap();
          return result;
        } catch (error) {
          return null;
        }
      });
      
      const users = await Promise.all(promises);
      const validUsers = users.filter(user => user !== null);
      
      return validUsers.map(user => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        avatar: user.photo?.path || `https://i.pravatar.cc/150?img=${user.id}`,
        profession: user.title || "Professional",
        rating: 4.8, // Mock rating - replace with actual data
        totalJobs: user.completedJobs || 0,
        location: user.address || "No location",
        isOnline: true, // Mock online status
        tags: user.skills || ["General Services"],
        description: user.bio || "Professional service provider",
        hourlyRate: user.hourlyRate || "$50",
        languages: user.languages || ["English"],
        verified: user.verified || false,
        backgroundChecked: user.backgroundChecked || false
      }));
    } catch (error) {
      return [];
    } finally {
      setProfessionalsLoading(false);
    }
  };

  // Use the fetched favorite professionals data
  const favoriteProfessionals = favoriteProfessionalsData;

  // Using menu items from SharedHeader4
  const sidebarItems = [
    { icon: <Home className="h-5 w-5" />, label: "Dashboard", path: "/clients" },
    { icon: <Users className="h-5 w-5" />, label: "Find Local Professionals", path: "/local-professionals" },
    { icon: <Globe className="h-5 w-5" />, label: "Find International Professionals", path: "/professional-international" },
    { icon: <FileText className="h-5 w-5" />, label: "Post a Job: Local", path: "/" },
    { icon: <FileText className="h-5 w-5" />, label: "Post a Job: International", path: "/internationalhompage" },
    { icon: <User2 className="h-5 w-5" />, label: "My Profile", path: "/client/profile" },
    { icon: <Settings2 className="h-5 w-5" />, label: "Settings", path: "/client/settings" },
    { icon: <MessageCircle className="h-5 w-5" />, label: "Messages", path: "/client/MessageAndVideoCall" },
    { icon: <FileCheck className="h-5 w-5" />, label: "My Orders", path: "/client/myorders" },
    { icon: <MdReviews className="h-5 w-5" />, label: "Reviews", path: "/client/reviews" },
    { icon: <Wallet className="h-5 w-5" />, label: "Wallet", path: "/client/Wallet" },
    { icon: <Shield className="h-5 w-5" />, label: "Support", path: "/client/support" },
    { icon: <LogOut className="h-5 w-5" />, label: "Log Out", path: "/auth/login" }
  ];

  const StoreLinks = () => (
    <div className="flex flex-col space-y-4 p-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Our App</h3>
        <p className="text-sm text-gray-600 mb-4">Get the best experience with our mobile app</p>
      </div>
      <div className="flex flex-col space-y-2">
        <button className="flex items-center justify-center gap-2 bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 12a2 2 0 114 0 2 2 0 01-4 0z"/>
          </svg>
          App Store
        </button>
        <button className="flex items-center justify-center gap-2 bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 12a2 2 0 114 0 2 2 0 01-4 0z"/>
          </svg>
          Google Play
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader4 />
      
      <div className="flex">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 bg-white shadow-sm min-h-screen">
          <div className="p-4">
            <nav className="space-y-2">
              {sidebarItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {item.icon}
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="mt-8">
              <StoreLinks />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Favorites</h1>
                <p className="text-gray-600 mt-1">
                  Your saved professionals for quick access
                </p>
              </div>
              <div className="flex items-center gap-2 lg:hidden">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setIsMobileMenuOpen(true)}
                >
                  <Menu className="h-5 w-5 mr-2" />
                  Menu
                </Button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mt-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("professionals")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "professionals"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Favorite Professionals
                </button>
              </nav>
            </div>

            {/* Professionals Tab Content */}
            {activeTab === "professionals" && (
              <>
                <div className="p-4 bg-blue-50 flex items-center gap-3 mt-6 rounded-lg">
                  <Search className="h-5 w-5 text-blue-600" />
                  <p className="text-sm text-blue-700">
                    These are professionals you&apos;ve favorited for quick access to their services
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {professionalsLoading ? (
                    <div className="col-span-full flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : favoriteProfessionals.length > 0 ? (
                    favoriteProfessionals.map((professional) => (
                      <Card key={professional.id} className="overflow-hidden hover:shadow-md transition-shadow">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <img 
                                src={professional.avatar} 
                                alt={professional.name} 
                                className="h-12 w-12 rounded-full object-cover border-2 border-white"
                              />
                              {professional.isOnline && (
                                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{professional.name}</h3>
                              <p className="text-sm text-gray-600">{professional.profession}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="ml-1 text-sm font-medium">{professional.rating}</span>
                            </div>
                            <span className="text-gray-300">|</span>
                            <span className="text-sm text-gray-600">{professional.totalJobs} jobs</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-3 flex items-center">
                            <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                            {professional.location}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {professional.tags.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex justify-between">
                          <Button size="sm" variant="outline">View Profile</Button>
                          <Button size="sm">Contact</Button>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-full p-8 text-center">
                      <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Star className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No favorite professionals yet</h3>
                        <p className="text-gray-600 mb-4">
                          Start exploring professionals and add them to your favorites for quick access
                        </p>
                        <div className="flex gap-2 justify-center">
                          <Button onClick={() => navigate("/local-professionals")}>
                            Find Local Professionals
                          </Button>
                          <Button variant="outline" onClick={() => navigate("/professional-international")}>
                            Find International Professionals
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <SharedFooter3 />
    </div>
  );
};

export default ClientFavorites;
